import { rememberAssetReloadAttempt, type AssetRecoveryDecision } from "./asset-load-diagnostics";

const VITE_PRELOAD_RECOVERY_BUILD_STORAGE_KEY = "jungle_vite_preload_recovery_build";
// The last "loaded build -> served deployment" reload attempted in this tab.
// If the same transition fails again, the reload and the probe disagree
// persistently (stale edge, split caching) and reloading would loop.
const VITE_PRELOAD_RECOVERY_TRANSITION_STORAGE_KEY = "jungle_vite_preload_recovery_transition";

export function normalizeBuildIdentifier(buildIdentifier: string): string {
  return buildIdentifier.trim() || "unknown";
}

// Observed by the entry so it can tell a failure the recovery is already
// handling (reload requested) from one it must render a fallback for.
let reloadRequested = false;
let preloadErrorsObserved = 0;
// The deployment probe decides asynchronously; the entry awaits it before
// rendering a fallback that would otherwise race the reload.
let pendingRecovery: Promise<boolean> | null = null;
let importsWithFallbackPending = 0;
const recoveredImportErrors = new WeakSet<object>();
let recoveryDecision: AssetRecoveryDecision = "not_observed";

/** Let a local fallback settle before the global handler reloads its page. */
export async function loadModuleWithPreloadFallback<T, F>(
  loadModule: () => Promise<T>,
  recoverLoadFailure: (error: unknown) => F
): Promise<T | F> {
  importsWithFallbackPending += 1;
  try {
    return await loadModule();
  } catch (error) {
    const fallback = recoverLoadFailure(error);
    // A throwing recovery callback does not claim the failure. Match the exact
    // Vite event payload, never another import that failed at the same time.
    if (error !== null && (typeof error === "object" || typeof error === "function")) {
      recoveredImportErrors.add(error);
    }
    return fallback;
  } finally {
    importsWithFallbackPending -= 1;
  }
}

export function getPreloadRecoveryState(): {
  reloadRequested: boolean;
  preloadErrorsObserved: number;
  pendingRecovery: Promise<boolean> | null;
  recoveryDecision: AssetRecoveryDecision;
} {
  return { reloadRequested, preloadErrorsObserved, pendingRecovery, recoveryDecision };
}

/** Test-only: forget observed failures and reload requests. */
export function resetPreloadRecoveryStateForTests(): void {
  reloadRequested = false;
  preloadErrorsObserved = 0;
  pendingRecovery = null;
  recoveryDecision = "not_observed";
}

type VitePreloadErrorRecoveryDependencies = {
  buildIdentifier: string;
  sessionStorage: Pick<Storage, "getItem" | "setItem">;
  reloadPage: () => void;
};

// The `vite:preloadError` event is never default-prevented here. Vite rethrows
// the original error into the dynamic import when it is not, so React.lazy
// rejects with "Failed to fetch dynamically imported module …" and the root
// error boundary (src/components/RootErrorBoundary.tsx) shows its reload
// screen and reports the failure once as a chunk miss. Preventing it made the
// import resolve to `undefined`, which surfaced as an engine-specific
// "reading 'default'" TypeError that had to be attributed by heuristics.

/**
 * Recover an open tab whose code-split asset disappeared after a deployment.
 * The build marker prevents a persistent asset or network failure from
 * creating a reload loop while still allowing a later deployment to recover.
 */
export function recoverFromVitePreloadError({
  buildIdentifier,
  sessionStorage,
  reloadPage,
}: VitePreloadErrorRecoveryDependencies): boolean {
  const normalizedBuildIdentifier = normalizeBuildIdentifier(buildIdentifier);

  try {
    if (sessionStorage.getItem(VITE_PRELOAD_RECOVERY_BUILD_STORAGE_KEY) === normalizedBuildIdentifier) {
      recoveryDecision = "build_already_attempted";
      return false;
    }

    sessionStorage.setItem(VITE_PRELOAD_RECOVERY_BUILD_STORAGE_KEY, normalizedBuildIdentifier);
  } catch {
    // Without a durable per-tab guard, reloading could loop indefinitely.
    recoveryDecision = "storage_unavailable";
    return false;
  }

  recoveryDecision = "first_failure";
  reloadPage();
  return true;
}

export function extractDeploymentIdentifierFromHtml(html: string): string | null {
  const deploymentMetaTag = html.match(
    /<meta\s+[^>]*name=["']jungle-deployment-id["'][^>]*content=["']([^"']+)["'][^>]*>/i
  );
  return deploymentMetaTag?.[1]?.trim() || null;
}

/**
 * The transition guard only exists to stop a reload that keeps landing on the
 * runtime it tried to leave. Once the runtime that starts is the transition's
 * target, the reload arrived and the guard has done its job; keeping it would
 * block a legitimate later recovery for the same pair, for example after a
 * rollback and re-promotion in the same tab.
 */
export function clearCompletedRecoveryTransition(
  buildIdentifier: string,
  sessionStorage: Pick<Storage, "getItem" | "removeItem">
): void {
  try {
    const transition = sessionStorage.getItem(VITE_PRELOAD_RECOVERY_TRANSITION_STORAGE_KEY);
    if (transition && transition.endsWith(`->${normalizeBuildIdentifier(buildIdentifier)}`)) {
      sessionStorage.removeItem(VITE_PRELOAD_RECOVERY_TRANSITION_STORAGE_KEY);
    }
  } catch {
    // Storage may be unavailable; the guard is then never stored either.
  }
}

/**
 * A second failure in the same build: reload only if a newer deployment is
 * actually being served, otherwise leave the failure to the error boundary.
 * Resolves to whether a reload was requested. Never throws.
 */
export async function recoverIfDeploymentChanged({
  buildIdentifier,
  sessionStorage,
  reloadPage,
  fetchCurrentHtml = () =>
    window.fetch(window.location.href, { cache: "no-store", headers: { Accept: "text/html" } }),
}: {
  buildIdentifier: string;
  sessionStorage: Pick<Storage, "getItem" | "setItem">;
  reloadPage: () => void;
  fetchCurrentHtml?: () => Promise<Response>;
}): Promise<boolean> {
  const normalizedBuildIdentifier = normalizeBuildIdentifier(buildIdentifier);
  recoveryDecision = "probe_pending";
  try {
    const response = await fetchCurrentHtml();
    if (!response.ok) throw new Error(`deployment probe returned ${response.status}`);

    const latestDeploymentIdentifier = extractDeploymentIdentifierFromHtml(await response.text());
    if (latestDeploymentIdentifier && latestDeploymentIdentifier !== normalizedBuildIdentifier) {
      const transition = `${normalizedBuildIdentifier}->${latestDeploymentIdentifier}`;
      if (sessionStorage.getItem(VITE_PRELOAD_RECOVERY_TRANSITION_STORAGE_KEY) === transition) {
        // Already reloaded for this exact transition and still failed on the
        // same runtime: reloading again would loop. Leave the boundary's screen.
        recoveryDecision = "transition_already_attempted";
        return false;
      }
      sessionStorage.setItem(VITE_PRELOAD_RECOVERY_TRANSITION_STORAGE_KEY, transition);
      // Keep the loaded runtime's marker so a repeat failure on this runtime
      // comes back here (and is then stopped by the transition guard); a newly
      // loaded runtime replaces the marker on its own first failure.
      sessionStorage.setItem(VITE_PRELOAD_RECOVERY_BUILD_STORAGE_KEY, normalizedBuildIdentifier);
      recoveryDecision = "deployment_changed";
      reloadPage();
      return true;
    }
    recoveryDecision = latestDeploymentIdentifier ? "deployment_unchanged" : "deployment_unknown";
  } catch {
    // The boundary already shows the original preload failure with a reload.
    recoveryDecision = "probe_failed";
  }
  return false;
}

/**
 * Registered from `src/index.tsx` before the app module graph is imported, so a
 * chunk that vanished mid-session is recovered even on the very first route.
 *
 * Deliberately reports nothing itself: `src/index.tsx` keeps the entry chunk
 * free of the Sentry SDK (it is loaded with `renderJungleWebApp`), and the
 * import failure reaches the root error boundary, which reports it once.
 */
export function initializeVitePreloadErrorRecovery({
  reloadPage = () => window.location.reload(),
}: { reloadPage?: () => void } = {}): () => void {
  if (typeof window === "undefined") return () => undefined;

  const buildIdentifier = normalizeBuildIdentifier(
    import.meta.env.VITE_DEPLOYMENT_ID || import.meta.env.VITE_GIT_SHA || import.meta.env.VITE_APP_VERSION || ""
  );
  try {
    // Reading `window.sessionStorage` itself throws in cookie-blocked contexts;
    // bootstrap must never depend on it.
    clearCompletedRecoveryTransition(buildIdentifier, window.sessionStorage);
  } catch {
    // Without storage no guard was ever stored either.
  }

  const attemptRecovery = (error: unknown): boolean | Promise<boolean> => {
    const requestReload = () => {
      reloadRequested = true;
      rememberAssetReloadAttempt(error, recoveryDecision);
      reloadPage();
    };
    try {
      if (window.sessionStorage.getItem(VITE_PRELOAD_RECOVERY_BUILD_STORAGE_KEY) === buildIdentifier) {
        return recoverIfDeploymentChanged({
          buildIdentifier,
          sessionStorage: window.sessionStorage,
          reloadPage: requestReload,
        });
      }

      return recoverFromVitePreloadError({
        buildIdentifier,
        // Reading `sessionStorage` itself throws in cookie-blocked contexts.
        sessionStorage: window.sessionStorage,
        reloadPage: requestReload,
      });
    } catch {
      // Recovery is best effort: the import still rejects with the original error.
      recoveryDecision = "storage_unavailable";
      return false;
    }
  };
  let stopped = false;
  const onPreloadError = (event: Event) => {
    preloadErrorsObserved += 1;
    const error = (event as Event & { payload?: unknown }).payload;
    const hasErrorIdentity = error !== null && (typeof error === "object" || typeof error === "function");
    const canUseLocalFallback = importsWithFallbackPending > 0 && hasErrorIdentity;
    // Vite emits before rejecting import(). Wait one task for the catch to
    // claim this error. Never wait for the import/network itself: a concurrent
    // hung optional import must not strand an unrelated route's recovery.
    const decision = canUseLocalFallback
      ? new Promise<void>((resolve) => setTimeout(resolve, 0)).then(() => {
        if (stopped) return false;
        if (recoveredImportErrors.has(error as object)) {
          recoveryDecision = "local_fallback";
          return false;
        }
        return attemptRecovery(error);
      })
      : attemptRecovery(error);
    if (decision instanceof Promise) {
      pendingRecovery = decision;
      void decision.finally(() => {
        if (pendingRecovery === decision) pendingRecovery = null;
      });
    }
  };
  window.addEventListener("vite:preloadError", onPreloadError);
  return () => {
    stopped = true;
    window.removeEventListener("vite:preloadError", onPreloadError);
  };
}
